/**
 * «Лиды Авито» и «Лиды ВК» на экране «Лиды»: обращения канала по дням — с чем
 * пришли (хотят платье / сдать своё / прочее) и сколько из желающих платье
 * записали («ждем клиента»). Клик по обращению — переписка под строкой;
 * «Дожим» — когда уходили видео салона и приглашение (два шага). Спека
 * `docs/specs/2026-09-27-leads-avito-design.md`; числа — те же, что у
 * `prokat-runner leads-avito [--source vkgroup]`.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { X } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { type Comment, Comments } from "@/components/Comments";
import { AvitoItems } from "@/pages/AvitoItems";
import { VkPosts } from "@/pages/VkPosts";
import type { ItemsGroup } from "@/lib/avitoItems";
import { DateRangePicker } from "@/components/DateRangePicker";
import { LeadLink } from "@/components/LeadLink";
import { FOLLOWUP_RUN, useCan } from "@/lib/access";
import { type ChatMessage, loadDialog } from "@/lib/bitrixChats";
import { iso, plusDays } from "@/lib/dates";
import {
  type AvitoDay,
  type AvitoLeads,
  type AvitoRow,
  type FollowupCheck,
  type FollowupDecision,
  type FollowupJob,
  type FollowupStage,
  type LeadSource,
  loadAvitoLeads,
  loadFollowup,
  loadOwnDialog,
  startFollowup,
  type Topic,
  addChatComment,
} from "@/lib/leadAvito";
import { shortTime } from "@/lib/leads";

const TOPICS: Topic[] = ["dress", "sell", "other"];
const TOPIC_CLASS: Record<Topic, string> = {
  dress: "bg-accent/15 text-accent-text",
  sell: "bg-warn-bg text-warn",
  other: "bg-bg-hover text-fg-muted",
};

function pct(a: number, b: number): string {
  return b ? `${Math.round((a / b) * 100)}%` : "—";
}

function dm(day: string): string {
  return day.slice(8, 10) + "." + day.slice(5, 7);
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
      data-annot={`лиды канала «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[22px] font-semibold leading-none tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function TopicBadge({ topic, name }: { topic: Topic; name: string }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11.5px] ${TOPIC_CLASS[topic]}`}
    >
      {name}
    </span>
  );
}

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

/** Подпись строки как в «Рассылках»: день «29.09 вт», неделя «22.09 – 28.09». */
function bucketTitle(from: string, to: string): string {
  return from === to
    ? `${dm(from)} ${WEEKDAY[new Date(`${from}T12:00:00`).getDay()]}`
    : `${dm(from)} – ${dm(to)}`;
}

type Group = "day" | "week" | "month";
const GROUP: Record<Group, { head: string; by: string; hint: string }> = {
  day: { head: "День", by: "по дням", hint: "День — дата начала диалога" },
  week: {
    head: "Неделя",
    by: "по неделям",
    hint: "Неделя — с понедельника по дню начала диалога, крайние обрезаны периодом",
  },
  month: {
    head: "Месяц",
    by: "по месяцам",
    hint: "Месяц — с 1-го числа по дню начала диалога, крайние обрезаны периодом (даты серым)",
  },
};

/** Подпись месяца «сентябрь 2026»; обрезанный периодом — с датами. */
function monthTitle(from: string, to: string): string {
  const d = new Date(`${from}T12:00:00`);
  const name = `${d.toLocaleDateString("ru-RU", { month: "long" })} ${from.slice(0, 4)}`;
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const full = from.slice(8) === "01" && Number(to.slice(8)) === last;
  return full ? name : `${name} · ${dm(from)} – ${dm(to)}`;
}

/** Ячейка «По дням»: что открыть в панели справа. */
type Filter =
  | "all"
  | "dress"
  | "phone"
  | "deal"
  | "booked"
  | "bron"
  | "contract"
  | "lost"
  | Lost;
type Lost = "lost_deal" | "lost_phone" | "lost_none";

/** «Не записали» без пересечений: есть сделка → есть телефон → ничего. */
function lostOf(x: AvitoRow): Lost | null {
  if (x.topic !== "dress" || x.booked_at) return null;
  return x.deal ? "lost_deal" : x.phone ? "lost_phone" : "lost_none";
}

const LOST: {
  id: Lost;
  label: string;
  hint: string;
  tone: string;
  bar: string;
}[] = [
  {
    id: "lost_deal",
    label: "сделка без записи",
    hint: "сделка есть, а в «ждем клиента» так и не попала",
    tone: "text-warn",
    bar: "bg-warn",
  },
  {
    id: "lost_phone",
    label: "телефон без сделки",
    hint: "номер оставили, новой сделки нет",
    tone: "text-warn",
    bar: "bg-warn/50",
  },
  {
    id: "lost_none",
    label: "без телефона",
    hint: "ни номера, ни сделки",
    tone: "text-fg",
    bar: "bg-fg-faint",
  },
];

function matches(x: AvitoRow, f: Filter): boolean {
  if (f === "all") return true;
  if (x.topic !== "dress") return false;
  switch (f) {
    case "dress":
      return true;
    case "phone":
      return x.phone;
    case "deal":
      return !!x.deal;
    case "booked":
      return !!x.booked_at;
    case "bron":
      return !!x.bron_at;
    case "contract":
      return !!x.contract_at;
    case "lost":
      return !x.booked_at;
    default:
      return lostOf(x) === f;
  }
}

type Cell = {
  from: string;
  to: string;
  filter: Filter;
  label: string;
  title: string;
};

/** Панель справа: обращения ячейки — та же таблица, что во вкладке «Обращения». */
function CellPanel({
  r,
  cell,
  by,
  byVideo,
  onComments,
  onClose,
}: {
  r: AvitoLeads;
  cell: Cell;
  by: Map<string, FollowupDecision> | null;
  byVideo: Map<string, FollowupDecision> | null;
  onComments: (chatId: number, list: Comment[]) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const rows = r.rows.filter(
    (x) => x.day >= cell.from && x.day <= cell.to && matches(x, cell.filter),
  );
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(80rem,100vw)] flex-col border-l border-border bg-bg shadow-xl"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        role="dialog"
        aria-label={`${cell.label} · ${cell.title}`}
        data-annot={`панель ${cell.title} ${cell.label}`}
      >
        <div className="flex flex-wrap items-baseline gap-3 border-b border-border px-4 py-3">
          <h2 className="text-[14px] font-semibold">
            {cell.label} · {cell.title}
          </h2>
          <span className="text-[12.5px] tabular-nums text-fg-muted">
            {rows.length} обращений
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto self-center rounded-[var(--radius-ui)] p-1 hover:bg-bg-hover"
            title="Закрыть (Esc)"
          >
            <X size={16} aria-hidden />
            <span className="sr-only">Закрыть</span>
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-4">
          <Rows
            r={r}
            rows={rows}
            by={by}
            byVideo={byVideo}
            onComments={onComments}
            title="Обращения"
          />
        </div>
      </aside>
    </>
  );
}

/** Счётчики строки: из ответа API + раскладка «не записали» по обращениям строки. */
type Bucket = {
  from: string;
  to: string;
  d: AvitoDay;
  lost: Record<Lost, number>;
};

function bucket(r: AvitoLeads, d: AvitoDay, from: string, to: string): Bucket {
  const lost = { lost_deal: 0, lost_phone: 0, lost_none: 0 };
  for (const x of r.rows) {
    const k = x.day >= from && x.day <= to ? lostOf(x) : null;
    if (k) lost[k] += 1;
  }
  return { from, to, d, lost };
}

/** Полоса строки: записали и места потерь «хотят платье», длина — от самого загруженного. */
function DayBar({ b, peak }: { b: Bucket; peak: number }) {
  if (!b.d.dress.total) return null;
  const parts = [
    { id: "booked", cls: "bg-ok", n: b.d.dress.booked },
    ...LOST.map((t) => ({ id: t.id, cls: t.bar, n: b.lost[t.id] })),
  ];
  return (
    <div
      className="flex h-2.5 overflow-hidden rounded-sm"
      style={{ width: `${(b.d.dress.total / peak) * 6}rem` }}
      aria-hidden
    >
      {parts.map((p) =>
        p.n ? (
          <span key={p.id} className={p.cls} style={{ flexGrow: p.n }} />
        ) : null,
      )}
    </div>
  );
}

/** «По дням»: воронка «хотят платье» строкой на день или неделю; клик по числу — обращения в панели справа. */
function Days({
  r,
  group,
  by,
  byVideo,
  onComments,
}: {
  r: AvitoLeads;
  group: Group;
  by: Map<string, FollowupDecision> | null;
  byVideo: Map<string, FollowupDecision> | null;
  onComments: (chatId: number, list: Comment[]) => void;
}) {
  const [cell, setCell] = useState<Cell | null>(null);
  const weeks = group === "week";
  const months = group === "month";
  const unit = GROUP[group];
  const dress = r.topics.dress;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const num = (
    b: Bucket,
    title: string,
    v: number,
    tone: string,
    f: Filter,
    label: string,
    of?: number,
  ) => (
    <button
      type="button"
      disabled={!v}
      onClick={() =>
        setCell({ from: b.from, to: b.to, filter: f, label, title })
      }
      className={`-mx-1.5 rounded-[var(--radius-ui)] px-1.5 py-0.5 text-left ${v ? "cursor-pointer hover:bg-bg-hover hover:ring-1 hover:ring-border-strong" : "cursor-default"}`}
      title={v ? `${label}: обращения — ${title}` : undefined}
    >
      <span
        className={`text-[14px] font-semibold ${v ? tone : "text-fg-faint"}`}
      >
        {v}
      </span>
      {of !== undefined && v > 0 && (
        <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, of)}</span>
      )}
    </button>
  );
  const td = "whitespace-nowrap px-3 py-1 tabular-nums";
  const cells = (b: Bucket, title: string, peak: number) => {
    const t = b.d.dress;
    return (
      <>
        <td className={td}>
          {num(b, title, b.d.total, "text-fg", "all", "Обращений")}
        </td>
        <td className={`${td} border-r border-border`}>
          {num(b, title, t.total, "text-fg", "dress", dress, b.d.total)}
        </td>
        <td className={td}>
          {num(
            b,
            title,
            t.phone,
            "text-fg",
            "phone",
            "Оставили телефон",
            t.total,
          )}
        </td>
        <td className={td}>
          {num(b, title, t.deal, "text-fg", "deal", "Есть сделка", t.total)}
        </td>
        <td className={td}>
          {num(b, title, t.booked, "text-ok", "booked", "Записали", t.total)}
        </td>
        <td className={td}>
          {num(b, title, t.bron, "text-ok", "bron", "Забронировали", t.total)}
        </td>
        <td className={td}>
          {num(
            b,
            title,
            t.contract,
            "text-ok",
            "contract",
            "Договор брони",
            t.total,
          )}
        </td>
        <td className={`${td} border-r border-border`}>
          {num(
            b,
            title,
            t.total - t.booked,
            "text-fg",
            "lost",
            "Не записали",
            t.total,
          )}
        </td>
        {LOST.map((x) => (
          <td key={x.id} className={td}>
            {num(b, title, b.lost[x.id], x.tone, x.id, x.label, t.total)}
          </td>
        ))}
        <td className="py-1.5 pl-3 pr-4">
          <DayBar b={b} peak={peak} />
        </td>
      </>
    );
  };
  const list = (months ? r.months : weeks ? r.weeks : r.days).map((d) =>
    bucket(r, d, d.day ?? "", d.to ?? d.day ?? ""),
  );
  const total = bucket(r, r.total, r.from, r.to);
  const peak = Math.max(1, ...list.map((b) => b.d.dress.total));
  return (
    <>
      <section
        className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
        data-annot={`${r.channel} ${unit.by}`}
      >
        <div className="overflow-x-auto">
          <table className="w-auto text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className={`${th} pl-4`} rowSpan={2}>
                  {unit.head}
                </th>
                <th className={th} rowSpan={2}>
                  Обращений
                </th>
                <th
                  className={`${th} border-r border-border`}
                  rowSpan={2}
                  title="прокат, примерка, покупка; % — от обращений"
                >
                  {dress}
                </th>
                <th
                  className={th}
                  rowSpan={2}
                  title="номер в лиде или в сообщении клиента"
                >
                  Телефон
                </th>
                <th
                  className={th}
                  rowSpan={2}
                  title="новая сделка по лиду или номеру, не раньше дня обращения"
                >
                  Сделка
                </th>
                <th
                  className={th}
                  rowSpan={2}
                  title="сделка хоть раз была в «ждем клиента» после начала диалога — по истории, не по стадии сейчас"
                >
                  Записали
                </th>
                <th
                  className={th}
                  rowSpan={2}
                  title="сделка хоть раз была в «Забронировано» после начала диалога"
                >
                  Забронировали
                </th>
                <th
                  className={th}
                  rowSpan={2}
                  title="договор сделки перешёл в «Бронь» / «Подписан» после начала диалога"
                >
                  Договор
                </th>
                <th
                  className={`${th} border-r border-border`}
                  rowSpan={2}
                  title="хотят платье, а в «ждем клиента» сделка так и не попала"
                >
                  Не записали
                </th>
                <th className={`${th} text-center`} colSpan={LOST.length}>
                  где потеряли
                </th>
                <th className={th} rowSpan={2} />
              </tr>
              <tr>
                {LOST.map((t) => (
                  <th
                    key={t.id}
                    className="whitespace-nowrap px-3 pb-2 font-normal"
                    title={t.hint}
                  >
                    {t.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((b) => {
                const wd = new Date(`${b.from}T12:00:00`).getDay();
                const title = months
                  ? monthTitle(b.from, b.to)
                  : bucketTitle(b.from, b.to);
                return (
                  <tr
                    key={b.from}
                    className={`border-t border-border ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                    data-annot={`${unit.head.toLowerCase()} ${b.from}`}
                  >
                    <td
                      className={`whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums ${b.d.total ? "" : "text-fg-faint"}`}
                    >
                      {title}
                    </td>
                    {cells(b, title, peak)}
                  </tr>
                );
              })}
              <tr
                className="border-t-2 border-border font-medium"
                data-annot="итого за период"
              >
                <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
                {cells(
                  total,
                  `за ${bucketTitle(r.from, r.to)}`,
                  Math.max(1, r.total.dress.total),
                )}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
          {unit.hint}; «{dress}» — доля от обращений, дальше доли — от «{dress}
          ». Записали, забронировали и договор — по истории сделки после начала
          диалога, не по стадии сейчас. «Записали» + «Не записали» = «{dress}»,
          «где потеряли» раскладывает «Не записали» без пересечений. Клик по
          числу — эти обращения справа, с перепиской и комментариями. Полоса:
          зелёный — записали, жёлтый — сделка без записи, бледно-жёлтый —
          телефон без сделки, серый — без телефона.
        </div>
      </section>
      {cell && (
        <CellPanel
          key={`${cell.from}-${cell.to}-${cell.filter}`}
          r={r}
          cell={cell}
          by={by}
          byVideo={byVideo}
          onComments={onComments}
          onClose={() => setCell(null)}
        />
      )}
    </>
  );
}

const WHO = {
  client: "Клиент",
  operator: "Оператор",
  system: "Портал",
} as const;

/** Переписка всех сессий обращения из своей копии (`GET /api/bitrix/chats/{id}`), без сообщений портала. */
/** Отчерк дня в переписке: «29 сентября, пн». */
function dayTitle(date: string): string {
  const d = new Date(`${date.slice(0, 10)}T12:00:00`);
  const wd = d.toLocaleDateString("ru-RU", { weekday: "short" });
  return `${d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}, ${wd}`;
}

function Dialog({ x }: { x: AvitoRow }) {
  // Обращение из своей ленты «Чатов» — один запрос; архив Bitrix — по сессиям.
  const own = x.own_chat;
  type Part = { messages: Pick<ChatMessage, "id" | "date" | "author" | "name" | "text">[] };
  const queries: { queryKey: unknown[]; queryFn: () => Promise<Part> }[] = own
    ? [
        {
          queryKey: ["own-dialog", own, x.at, x.own_to],
          queryFn: () => loadOwnDialog(own, x.at, x.own_to),
        },
      ]
    : x.session_ids.map((id) => ({
        queryKey: ["bitrix-dialog", id],
        queryFn: () => loadDialog(id),
      }));
  const qs = useQueries({ queries });
  if (qs.some((q) => q.isPending)) return <span>Загружаю переписку…</span>;
  const bad = qs.find((q) => q.isError);
  if (bad)
    return (
      <span className="text-err">
        Не удалось получить переписку: {String(bad.error?.message)}
      </span>
    );
  const msgs = qs
    .flatMap((q) => q.data?.messages ?? [])
    .filter((m) => m.author !== "system");
  if (msgs.length === 0)
    return (
      <span>
        Только сообщения портала — клиент и оператор ничего не написали.
      </span>
    );
  return (
    <>
      {msgs.map((m, i) => (
        <Fragment key={m.id}>
          {m.date.slice(0, 10) !== msgs[i - 1]?.date.slice(0, 10) && (
            <div
              className={`flex items-center gap-2 text-[11.5px] font-medium text-fg-faint ${i ? "mt-2" : ""}`}
            >
              {dayTitle(m.date)}
              <span className="h-px flex-1 bg-border" />
            </div>
          )}
          <div>
            <span className="tabular-nums">{m.date.slice(11, 16)}</span>{" "}
            <span
              className={
                m.author === "client" ? "font-medium text-fg" : "text-fg-muted"
              }
            >
              {m.author === "operator" && m.name ? m.name : WHO[m.author]}:
            </span>{" "}
            <span className={m.author === "client" ? "text-fg" : ""}>
              {m.text || "(файл или пусто)"}
            </span>
          </div>
        </Fragment>
      ))}
    </>
  );
}

/** «Дожим» строки: уже был — когда; после проверки — «уйдёт» или почему нет. */
function FollowupCell({
  x,
  by,
}: {
  x: AvitoRow;
  by: Map<string, FollowupDecision> | null;
}) {
  const d = by ? decisionOf(x, by) : undefined;
  if (d && !d.reason) return <span className="font-medium text-ok">уйдёт</span>;
  // Текст — если последний дожим позже видео (видео — своя колонка).
  const text =
    x.followup_at && x.followup_at !== x.followup_video_at
      ? x.followup_at
      : null;
  if (text)
    return (
      <span className="flex flex-col">
        <span>текст {shortTime(text)}</span>
        {d && <span className="text-fg-faint">{reasonText(d)}</span>}
      </span>
    );
  if (d) return <span className="text-fg-faint">{reasonText(d)}</span>;
  return (
    <span className="text-fg-faint">{by ? "не в проверке" : "не было"}</span>
  );
}

/** «Видео»: ушло по журналу; иначе — решение последней проверки шага «видео». */
function VideoCell({
  x,
  by,
}: {
  x: AvitoRow;
  by: Map<string, FollowupDecision> | null;
}) {
  if (x.followup_video_at)
    return (
      <span className="text-ok">
        отправлено {shortTime(x.followup_video_at)}
      </span>
    );
  const d = by ? decisionOf(x, by) : undefined;
  if (d && !d.reason)
    return <span className="font-medium text-warn">нужно отправить</span>;
  if (d?.reason === "видео было")
    return <span className="text-fg-muted">было в чате</span>;
  if (d) return <span className="text-fg-faint">{reasonText(d)}</span>;
  return (
    <span className="text-fg-faint">
      {x.topic === "dress" ? "не проверено" : "—"}
    </span>
  );
}

/** Кто написал последним: «клиент» — ждёт нас, «мы» — ждём клиента. */
function LastCell({ x }: { x: AvitoRow }) {
  if (!x.last) return <span className="text-fg-faint">—</span>;
  const мы = x.last.author === "operator";
  return (
    <span title={x.last.text}>
      <span
        className={`mr-1.5 whitespace-nowrap font-medium ${мы ? "text-fg-muted" : "text-warn"}`}
      >
        {мы ? "мы" : "клиент"}
        {x.last.at && (
          <span className="ml-1 font-normal tabular-nums text-fg-faint">
            {shortTime(x.last.at)}
          </span>
        )}
      </span>
      <span className="text-fg-muted">{x.last.text}</span>
    </span>
  );
}

const COLUMNS = [
  "Начало",
  "Клиент",
  "Комментарий",
  "Тема",
  "Что написал",
  "Последнее",
  "Лид",
  "Сделка",
  "Запись",
  "Дожим",
  "Видео",
];

function Rows({
  r,
  rows,
  title,
  by,
  byVideo,
  onComments,
}: {
  r: AvitoLeads;
  rows: AvitoRow[];
  title: string;
  by: Map<string, FollowupDecision> | null;
  byVideo: Map<string, FollowupDecision> | null;
  onComments: (chatId: number, list: Comment[]) => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
      data-annot={`обращения ${r.channel}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">
          {title}: {rows.length}
        </h2>
        <span className="text-[12px] text-fg-faint">
          нажмите на обращение — откроется переписка; тема — по разметке модели,
          «правила» — ещё не размечено
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Обращений нет.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                {COLUMNS.map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-3 py-2 font-medium"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const on = open === x.session_id;
                return (
                  <Fragment key={x.session_id}>
                    <tr
                      onClick={() => setOpen(on ? null : x.session_id)}
                      title={on ? "Свернуть переписку" : "Показать переписку"}
                      className={`cursor-pointer border-t border-border align-top hover:bg-bg-hover ${on ? "bg-bg-hover" : ""}`}
                      data-annot={`диалог ${x.session_id}`}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                        <span className="mr-1 inline-block w-2 text-fg-faint">
                          {on ? "▾" : "▸"}
                        </span>
                        {shortTime(x.at)}
                      </td>
                      <td className="max-w-[200px] px-3 py-1.5" title={x.name}>
                        <div className="flex items-baseline gap-1.5">
                          <span className="truncate">
                            {x.name || <span className="text-fg-faint">—</span>}
                          </span>
                          {x.chats.map((c) => (
                            <a
                              key={c}
                              href={chatUrl(r.source, c)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={`открыть чат в ${r.source === "vkgroup" ? "ВКонтакте" : "Авито"}`}
                              className="shrink-0 text-[11.5px] text-accent hover:underline"
                            >
                              {r.source === "vkgroup" ? "ВК" : "Авито"} ↗
                            </a>
                          ))}
                          {x.own_chat && (
                            <Link
                              to={`/chats/${x.own_chat}`}
                              target="_blank"
                              onClick={(e) => e.stopPropagation()}
                              title="открыть диалог в «Чатах»"
                              className="shrink-0 text-[11.5px] text-accent hover:underline"
                            >
                              Чаты ↗
                            </Link>
                          )}
                        </div>
                      </td>
                      <td
                        className="cursor-auto px-3 py-1.5"
                        title=""
                        onClick={(e) => e.stopPropagation()}
                        data-annot={`комментарии чата ${x.chat_id}`}
                      >
                        <Comments
                          list={x.comments}
                          add={(text) => addChatComment(x.chat_id, text)}
                          onSaved={(list) => onComments(x.chat_id, list)}
                          placeholder="что сделали с чатом… Enter — сохранить"
                          annot={`добавить комментарий ${x.chat_id}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <TopicBadge topic={x.topic} name={r.topics[x.topic]} />
                        {x.topic_source === "rules" && (
                          <span className="ml-1 text-[11px] text-fg-faint">
                            правила
                          </span>
                        )}
                      </td>
                      <td className="min-w-[260px] px-3 py-1.5 text-fg-muted">
                        {x.quote || (
                          <span className="text-fg-faint">
                            {x.fetched
                              ? "клиент ничего не написал"
                              : "переписка не скачана"}
                          </span>
                        )}
                      </td>
                      <td className="min-w-[220px] max-w-[320px] px-3 py-1.5">
                        <div className="line-clamp-2">
                          <LastCell x={x} />
                        </div>
                      </td>
                      <td
                        className="px-3 py-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {x.lead_id ? (
                          <span className="whitespace-nowrap">
                            <LeadLink id={x.lead_id} />
                            {x.lead_status && (
                              <span className="ml-1.5 text-fg-muted">
                                {x.lead_status}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {x.deal ? (
                          <>
                            <Link
                              to={`/deals/${x.deal.id}`}
                              className="font-mono underline"
                            >
                              {x.deal.id}
                            </Link>
                            <span className="ml-1.5 text-fg-muted">
                              {x.deal.stage_name ?? x.deal.stage}
                            </span>
                          </>
                        ) : x.no_lead ? (
                          <span
                            className="text-warn"
                            title="Хочет платье, а ни лида, ни новой сделки нет — обращение мимо воронки"
                          >
                            нет лида
                          </span>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                        {x.old_deal && (
                          <div
                            className="text-xs text-fg-muted"
                            title="Bitrix сам привязал чат к незакрытой сделке контакта; в воронку обращения не входит"
                          >
                            привязан к старой{" "}
                            <Link
                              to={`/deals/${x.old_deal.id}`}
                              className="font-mono underline"
                            >
                              {x.old_deal.id}
                            </Link>{" "}
                            · {x.old_deal.stage_name ?? x.old_deal.stage} ·{" "}
                            {x.old_deal.created
                              .slice(0, 10)
                              .split("-")
                              .reverse()
                              .join(".")}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                        {x.booked_at ? (
                          <span className="text-ok">
                            {shortTime(x.booked_at)}
                          </span>
                        ) : (
                          <span className="text-fg-faint">—</span>
                        )}
                        {x.bron_at && (
                          <div
                            className="text-xs text-fg-muted"
                            title="сделка перешла в «Забронировано»"
                          >
                            бронь {shortTime(x.bron_at)}
                          </div>
                        )}
                        {x.contract_at && (
                          <div
                            className="text-xs text-fg-muted"
                            title="договор перешёл в «Бронь» / «Подписан»"
                          >
                            договор {shortTime(x.contract_at)}
                          </div>
                        )}
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-1.5 tabular-nums"
                        title="последний автодожим чату или лиду: видео салона + приглашение"
                      >
                        <FollowupCell x={x} by={by} />
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-1.5"
                        title="видео салона: отправлено по журналу; иначе — последняя проверка шага «Видео»"
                      >
                        <VideoCell x={x} by={byVideo} />
                      </td>
                    </tr>
                    {on && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="bg-bg pb-3 pl-8 pr-4 pt-2 text-[12px] leading-[1.5]"
                          data-annot={`переписка ${x.session_id}`}
                        >
                          <div className="whitespace-pre-wrap border-l-2 border-border pl-3 text-fg-muted">
                            <Dialog x={x} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Шаги дожима (владелец 30.09.26): сначала видео, потом отдельно — вопрос о записи. */
const STAGES: Record<
  FollowupStage,
  { title: string; days: number; hint: string }
> = {
  video: {
    title: "1 · Видео салона",
    days: 3,
    hint:
      "только видео, сразу, без ожидания. Уйдёт тем, кто хочет платье (клиент писал за 3 дня):" +
      " без лида, с лидом «В работе» / «Нет потребности» или сделкой «Новая», «В работе»;" +
      " видео ещё не было. Кого уже записывали на примерку — не трогаем",
  },
  text: {
    title: "2 · Вопрос о записи",
    days: 7,
    hint:
      "«Хотите записаться на примерку?» — ждём диалога. Уйдёт тем, у кого лид «В работе», или хочет" +
      " платье и лида нет / лид «Нет потребности» / сделка «Новая», «В работе»; последними писали" +
      " мы, клиент молчит 24 ч, текста ещё не было. Кто видео не получал — сначала видео." +
      " Кого уже записывали на примерку — не трогаем",
  },
};

/** Почему рассылка не пишет этому чату — по-человечески. */
function reasonText(d: FollowupDecision): string {
  const st = d.status_name ?? d.status ?? "—";
  switch (d.reason) {
    case "статус":
      return `лид «${st}»`;
    case "стадия":
      return `сделка «${st}»`;
    case "нет лида":
      return d.old_deal
        ? `нет лида; сделка ${d.old_deal} — старая, привязал Bitrix`
        : "клиент в «Чаты» не писал, лида и сделки нет";
    case "была запись":
      return `уже записывали, сделка «${st}»`;
    case "тема":
      return "не про платье";
    case "без темы":
      return "тема не определена";
    case "видео было":
      return "видео уже отправляли";
    case "старое обращение":
      return "обращение старше 3 дн.";
    case "журнал":
      return "текст уже отправляли";
    case "уже писали":
      return "приглашение уже в чате";
    case "клиент ждёт":
      return "ждёт ответа оператора";
    case "рано":
      return "молчит меньше 24 ч";
    case "пусто":
      return "в чате пусто";
    case "тот же клиент":
      return "ушло в другой чат клиента";
    case "только чтение":
      return "чат только для чтения";
    case "сверх лимита":
      return "сверх лимита за раз";
    default:
      return d.reason;
  }
}

/** Чат в кабинете площадки: Авито — канал мессенджера, ВК — диалог сообщества. */
function chatUrl(source: LeadSource, chat: string): string {
  return source === "vkgroup"
    ? `https://vk.ru/gim115095885/convo/${chat}`
    : `https://www.avito.ru/profile/messenger/channel/${chat}`;
}

/** Последним в чате написал клиент — ждёт нас. */
function clientWaits(x: AvitoRow): boolean {
  return x.last?.author === "client";
}

/** До записи: сделки ещё нет или она «Новая» / «В работе». */
const PREBOOK_STAGES = new Set(["NEW", "3"]);
function beforeBooking(x: AvitoRow): boolean {
  return !x.deal || PREBOOK_STAGES.has(x.deal.stage);
}

/** Решение рассылки по строке: из её чатов — «уйдёт», если хоть один проходит. */
function decisionOf(
  x: AvitoRow,
  by: Map<string, FollowupDecision>,
): FollowupDecision | undefined {
  const ds = x.chats
    .map((c) => by.get(c))
    .filter((d): d is FollowupDecision => !!d);
  return ds.find((d) => !d.reason) ?? ds[0];
}

/** Запуск дожима: «Проверить» считает решение по каждому чату (таблица ниже фильтруется),
 *  «Отправить» шлёт в фоне; вывод команды — свёрнут под кнопками. */
function Followup({
  source,
  name,
  job,
  onChecked,
}: {
  source: LeadSource;
  name: string;
  job: FollowupJob | null | undefined;
  onChecked: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [stage, setStage] = useState<FollowupStage>(
    job?.source === source && job.stage ? job.stage : "video",
  );
  const st = STAGES[stage];
  const run = useMutation({
    mutationFn: (send: boolean) => startFollowup(source, send, stage),
    onSuccess: (d, send) => {
      qc.setQueryData(["leads-followup"], d);
      setConfirm(false);
      setWaiting(!send);
    },
  });
  useEffect(() => {
    if (waiting && job && !job.running && !job.send && job.source === source) {
      setWaiting(false);
      onChecked();
    }
  }, [waiting, job, source, onChecked]);
  useEffect(() => {
    if (job && !job.running)
      qc.invalidateQueries({ queryKey: ["leads-avito"] });
  }, [job?.running, job, qc]);
  const busy = !!job?.running || run.isPending;
  const btn =
    "h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] disabled:opacity-50";
  const mine = job?.source === source;
  return (
    <section
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
      data-annot={`дожим ${name}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <h2 className="text-[13px] font-medium">Дожим</h2>
        <div
          className="flex gap-1"
          role="tablist"
          aria-label="Шаг дожима"
          data-annot="шаг дожима"
        >
          {(Object.keys(STAGES) as FollowupStage[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={stage === k}
              disabled={busy}
              onClick={() => {
                setStage(k);
                setConfirm(false);
              }}
              className={`${btn} ${
                stage === k
                  ? "border-accent text-fg"
                  : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {STAGES[k].title} · {STAGES[k].days} дн.
            </button>
          ))}
        </div>
        <span className="basis-full text-[12px] text-fg-faint">{st.hint}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => run.mutate(false)}
            className={`${btn} border-border text-fg-muted hover:text-fg`}
          >
            Проверить, кому уйдёт
          </button>
          {confirm ? (
            <>
              <span className="text-[12px] text-warn">
                Сообщения уйдут клиентам в боевой {name}.
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => run.mutate(true)}
                className={`${btn} border-warn bg-warn-bg text-warn`}
              >
                Да, отправить
              </button>
              <button
                type="button"
                onClick={() => setConfirm(false)}
                className={`${btn} border-border text-fg-muted`}
              >
                Отмена
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm(true)}
              className={`${btn} border-accent text-fg`}
            >
              Отправить дожим
            </button>
          )}
        </div>
      </div>
      {run.isError && (
        <div className="border-t border-border px-4 py-2 text-[12.5px] text-err">
          {String(run.error.message)}
        </div>
      )}
      {job && (
        <details
          className="border-t border-border px-4 py-2"
          open={job.running && job.send}
          data-annot="вывод дожима"
        >
          <summary className="cursor-pointer text-[12px] text-fg-muted">
            {job.source === "vkgroup" ? "ВКонтакте" : "Авито"} ·{" "}
            {job.stage ? `${STAGES[job.stage].title} · ` : ""}
            {job.send ? "отправка" : "проверка"} · {shortTime(job.started_at)} ·{" "}
            {job.running ? (
              <span className="text-warn">
                идёт… {job.send ? "" : "(проверка чатов — около минуты)"}
              </span>
            ) : job.result && mine ? (
              `уйдёт ${job.result.decisions.filter((d) => !d.reason).length} из ${job.result.decisions.length} чатов — см. таблицу ниже`
            ) : (
              "завершено"
            )}{" "}
            · вывод команды
          </summary>
          <pre className="mt-1 max-h-[320px] overflow-auto whitespace-pre-wrap text-[11.5px] leading-[1.45] text-fg-muted">
            {job.output.join("\n") || "…"}
          </pre>
        </details>
      )}
    </section>
  );
}

export function LeadAvitoSection({
  source = "avito",
}: {
  source?: LeadSource;
}) {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -13));
  const to = params.get("to") || iso(today);
  const day = params.get("day") || "";
  const topic = (params.get("topic") || "") as Topic | "";
  const need = params.get("need") === "1";
  const waits = params.get("waits") === "1";
  const prebook = params.get("prebook") === "1";
  const view =
    params.get("view") === "days"
      ? "days"
      : params.get("view") === "items" && source === "avito"
        ? "items"
        : params.get("view") === "posts" && source === "vkgroup"
          ? "posts"
          : "chats";
  const itemsBy: ItemsGroup =
    params.get("by") === "city" || params.get("by") === "title"
      ? (params.get("by") as ItemsGroup)
      : "item";
  const group: Group =
    params.get("group") === "week" || params.get("group") === "month"
      ? (params.get("group") as Group)
      : "day";
  // Неделя из «По неделям»: понедельник; строки — с него по воскресенье.
  const week = params.get("week") || "";
  const weekEnd = week ? iso(plusDays(new Date(`${week}T12:00:00`), 6)) : "";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const key = ["leads-avito", source, from, to] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => loadAvitoLeads(from, to, source),
    // «Объявления» считают обращения на сервере сами
    enabled: view !== "items" && view !== "posts",
  });
  const qc = useQueryClient();
  // сохранённый комментарий кладём во все обращения чата, не пересчитывая период
  const onComments = (chatId: number, list: Comment[]) =>
    qc.setQueryData<AvitoLeads>(
      key,
      (d) =>
        d && {
          ...d,
          rows: d.rows.map((x) =>
            x.chat_id === chatId ? { ...x, comments: list } : x,
          ),
        },
    );
  const fq = useQuery({
    queryKey: ["leads-followup"],
    queryFn: loadFollowup,
    refetchInterval: (x) => (x.state.data?.job?.running ? 3000 : false),
  });
  const job = fq.data?.job;
  // Решения — только свежей проверки этого канала.
  const result =
    job && !job.running && !job.send && job.source === source
      ? job.result
      : null;
  const by = new Map((result?.decisions ?? []).map((d) => [d.chat, d]));
  const videoCheck: FollowupCheck | undefined =
    fq.data?.checks?.[`${source}:video`];
  const byVideo = videoCheck
    ? new Map(videoCheck.decisions.map((d) => [d.chat, d]))
    : null;
  const needVideo = params.get("video") === "1";
  const onChecked = useCallback(() => set({ need: "1" }), [params]);
  const name = source === "vkgroup" ? "ВКонтакте" : "Авито";
  // запуск дожима пишет клиентам в боевой мессенджер — только с правом
  const canFollowup = useCan(FOLLOWUP_RUN);

  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  // Шапка — вне загрузки: период и группировку можно сменить, пока год считается.
  const header = (
    <div className="flex flex-col gap-3" data-annot={`период лиды ${name}`}>
      <div className="flex gap-1" role="tablist" aria-label={`Лиды ${name}`}>
        {(
          [
            [
              "chats",
              q.data ? `Обращения · ${q.data.total.total}` : "Обращения",
            ],
            ["days", "По дням"],
            ...(source === "avito" ? ([["items", "Объявления"]] as const) : []),
            ...(source === "vkgroup" ? ([["posts", "Посты"]] as const) : []),
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={view === k}
            onClick={() => set({ view: k === "chats" ? "" : k })}
            className={seg(view === k)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker
          label="Период"
          from={from}
          to={to}
          onChange={(a, b) => set({ from: a, to: b, day: "", week: "" })}
        />
        {view === "days" && (
          <div className="flex gap-1" role="group" aria-label="Группировка">
            {(
              [
                ["day", "по дням"],
                ["week", "по неделям"],
                ["month", "по месяцам"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => set({ group: k === "day" ? "" : k })}
                aria-pressed={group === k}
                className={seg(group === k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (view === "items")
    return (
      <>
        {header}
        <AvitoItems
          from={from}
          to={to}
          group={itemsBy}
          sort={params.get("sort") || ""}
          set={set}
        />
      </>
    );
  if (view === "posts")
    return (
      <>
        {header}
        <VkPosts from={from} to={to} />
      </>
    );
  if (q.isPending)
    return (
      <>
        {header}
        <div className="text-[12.5px] text-fg-muted">
          Считаю диалоги {name}…
        </div>
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <div className="text-[12.5px] text-err">
          Не удалось получить лиды {name}: {String(q.error.message)}
        </div>
      </>
    );
  const r = q.data;
  const t = r.total;
  const goes = (x: AvitoRow) => decisionOf(x, by)?.reason === "";
  const videoDue = (x: AvitoRow) =>
    !x.followup_video_at && !!byVideo && decisionOf(x, byVideo)?.reason === "";
  const base = r.rows.filter(
    (x) =>
      (!day || x.day === day) &&
      (!week || (x.day >= week && x.day <= weekEnd)) &&
      (!topic || x.topic === topic),
  );
  const rows = base.filter(
    (x) =>
      (!need || goes(x)) &&
      (!needVideo || videoDue(x)) &&
      (!waits || clientWaits(x)) &&
      (!prebook || beforeBooking(x)),
  );
  const toggle = (
    on: boolean,
    key: string,
    label: string,
    n: number,
    hint: string,
  ) => (
    <button
      type="button"
      onClick={() => set({ [key]: on ? "" : "1" })}
      aria-pressed={on}
      title={hint}
      className={`h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
        on
          ? "border-accent text-fg"
          : "border-border text-fg-muted hover:text-fg"
      }`}
    >
      {label} · {n}
    </button>
  );
  const needCount = r.rows.filter(goes).length;

  return (
    <>
      {header}

      {view === "chats" && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat
            label="Обращений"
            value={t.total}
            hint={`${dm(r.from)} — ${dm(r.to)}; моделью размечено ${t.by_model}`}
          />
          <Stat
            label={r.topics.dress}
            value={t.topics.dress}
            hint={`${pct(t.topics.dress, t.total)} обращений`}
          />
          <Stat
            label={r.topics.sell}
            value={t.topics.sell}
            hint={`${pct(t.topics.sell, t.total)} обращений`}
          />
          <Stat
            label={r.topics.other}
            value={t.topics.other}
            hint="вакансии, реклама, пустые диалоги"
          />
          <Stat
            label="Дожим"
            value={t.followup}
            hint="видео салона + приглашение ушли чату или лиду"
          />
          <Stat
            label="Записали"
            value={`${t.dress.booked} из ${t.dress.total}`}
            hint={`${pct(t.dress.booked, t.dress.total)} желающих платье · бронь ${t.dress.bron} · договор ${t.dress.contract} · без лида ${t.dress.no_lead}`}
          />
        </div>
      )}

      {view === "days" ? (
        <Days
          r={r}
          group={group}
          by={result ? by : null}
          byVideo={byVideo}
          onComments={onComments}
        />
      ) : (
        <>
          {canFollowup && (
            <Followup
              source={source}
              name={name}
              job={job}
              onChecked={onChecked}
            />
          )}
          <div
            className="flex flex-wrap items-center gap-1"
            role="group"
            aria-label="Тема"
          >
            {(["", ...TOPICS] as const).map((k) => (
              <button
                key={k || "all"}
                type="button"
                onClick={() => set({ topic: k })}
                aria-pressed={topic === k}
                className={`h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
                  topic === k
                    ? "border-accent text-fg"
                    : "border-border text-fg-muted hover:text-fg"
                }`}
              >
                {k ? r.topics[k] : "все темы"}
              </button>
            ))}
            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
            {toggle(
              waits,
              "waits",
              "клиент ждёт ответа",
              base.filter(clientWaits).length,
              "последним в чате написал клиент",
            )}
            {toggle(
              prebook,
              "prebook",
              "до записи",
              base.filter(beforeBooking).length,
              "сделки нет или она «Новая» / «В работе»",
            )}
            {videoCheck &&
              toggle(
                needVideo,
                "video",
                "нужно видео",
                base.filter(videoDue).length,
                `по проверке шага «Видео» ${shortTime(videoCheck.checked_at)}: видео ещё не уходило`,
              )}
            {result && (
              <button
                type="button"
                onClick={() => set({ need: need ? "" : "1" })}
                aria-pressed={need}
                title={`по проверке ${shortTime(result.checked_at)}: кому уйдёт дожим`}
                className={`h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
                  need
                    ? "border-accent text-fg"
                    : "border-border text-fg-muted hover:text-fg"
                }`}
              >
                уйдёт дожим · {needCount}
              </button>
            )}
            {day && (
              <button
                type="button"
                onClick={() => set({ day: "" })}
                title="снять фильтр дня"
                className="h-8 rounded-[var(--radius-ui)] border border-accent px-2.5 text-[12.5px] text-fg"
              >
                день {dm(day)} ×
              </button>
            )}
            {week && (
              <button
                type="button"
                onClick={() => set({ week: "" })}
                title="снять фильтр недели"
                className="h-8 rounded-[var(--radius-ui)] border border-accent px-2.5 text-[12.5px] text-fg"
              >
                неделя {dm(week)} — {dm(weekEnd)} ×
              </button>
            )}
          </div>
          <Rows
            r={r}
            rows={need && result ? rows : need ? [] : rows}
            by={result ? by : null}
            byVideo={byVideo}
            onComments={onComments}
            title={
              need
                ? `Уйдёт дожим${result ? ` (проверка ${shortTime(result.checked_at)})` : " — нажмите «Проверить, кому уйдёт»"}`
                : day
                  ? `Обращения ${dm(day)}`
                  : week
                    ? `Обращения ${dm(week)} — ${dm(weekEnd)}`
                    : "Все обращения периода"
            }
          />
        </>
      )}
    </>
  );
}
